import { CloudSyncOutlined } from '@ant-design/icons';
import type { SpvExpenseSyncItem, SpvExpenseSyncResult, SpvExpenseSyncStatus } from '@financiar/shared';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Modal, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { DateText, MoneyText } from '../../lib/format';
import { syncSpvReceived } from './api';

const { Text } = Typography;

const STATUS_TAG: Record<SpvExpenseSyncStatus, { color: string; label: string }> = {
  created: { color: 'green', label: 'Adăugată' },
  exists: { color: 'default', label: 'Există deja' },
  unsupported: { color: 'gold', label: 'Neimportabilă' },
  failed: { color: 'red', label: 'Eșuată' },
};

/**
 * "Sincronizare SPV" — pulls the RECEIVED invoices (facturi primite) of the
 * firm's SPV inbox into expenses. Direct-apply with a detailed per-message
 * result list, the same UX as the sent-side e-Factura sync. ANAF keeps ~60
 * days; older history comes through the SmartBill import instead.
 */
export function SpvSyncModal({
  open,
  firmaId,
  onClose,
  onDone,
}: {
  open: boolean;
  firmaId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { message } = App.useApp();
  const [result, setResult] = useState<SpvExpenseSyncResult | null>(null);

  const syncMutation = useMutation({
    mutationFn: () => syncSpvReceived(firmaId, 60),
    onSuccess: (data) => {
      setResult(data);
      if (data.created > 0) onDone();
    },
    onError: (error) => {
      message.error(getApiError(error)?.message ?? 'Sincronizarea cu SPV a eșuat.');
    },
  });

  function handleClose() {
    setResult(null);
    onClose();
  }

  const columns: ColumnsType<SpvExpenseSyncItem> = [
    { title: 'Mesaj SPV', key: 'createdAt', width: 110, render: (_, row) => <DateText value={row.createdAt?.slice(0, 10) ?? null} /> },
    { title: 'Număr factură', dataIndex: 'invoiceNumber', width: 160, ellipsis: true, render: (value: string | null) => value ?? '—' },
    { title: 'Furnizor', dataIndex: 'supplierName', ellipsis: true, render: (value: string | null) => value ?? '—' },
    {
      title: 'Total',
      key: 'total',
      width: 140,
      align: 'right',
      render: (_, row) => (row.total ? <MoneyText value={row.total} currency={row.currency ?? 'RON'} /> : '—'),
    },
    {
      title: 'Rezultat',
      key: 'status',
      width: 130,
      render: (_, row) => {
        const { color, label } = STATUS_TAG[row.status];
        const tag = <Tag color={color}>{label}</Tag>;
        return row.reason ? <Tooltip title={row.reason}>{tag}</Tooltip> : tag;
      },
    },
  ];

  return (
    <Modal
      title="Sincronizează facturile primite din SPV"
      open={open}
      onCancel={handleClose}
      width={880}
      destroyOnHidden
      footer={[
        <Button key="close" onClick={handleClose}>
          Închide
        </Button>,
        <Button
          key="sync"
          type="primary"
          icon={<CloudSyncOutlined />}
          loading={syncMutation.isPending}
          onClick={() => syncMutation.mutate()}
        >
          {result ? 'Sincronizează din nou' : 'Sincronizează (ultimele 60 de zile)'}
        </Button>,
      ]}
    >
      <Spin spinning={syncMutation.isPending} tip="Se descarcă facturile primite din SPV…">
        {!result ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              Se listează mesajele „FACTURA PRIMITA" din SPV (ANAF păstrează ~60 de zile), se descarcă XML-urile și se creează
              cheltuieli <Text strong>neplătite</Text>, cu furnizorul și fișierul XML atașat.
            </Text>
            <Text type="secondary">
              Reconcilierea cu plățile se face la importul extrasului bancar sau manual, din „Tranzacții nereconciliate".
            </Text>
          </Space>
        ) : (
          <>
            <Alert
              style={{ marginBottom: 16 }}
              type={result.failed > 0 ? 'warning' : 'success'}
              showIcon
              message={`${result.total} mesaje: ${result.created} adăugate, ${result.skippedExisting} existente, ${result.failed} neimportate.`}
            />
            <Table<SpvExpenseSyncItem>
              rowKey="downloadId"
              size="small"
              columns={columns}
              dataSource={result.items}
              pagination={false}
              scroll={{ y: 400 }}
              locale={{ emptyText: 'Niciun mesaj „FACTURA PRIMITA" în intervalul selectat.' }}
            />
          </>
        )}
      </Spin>
    </Modal>
  );
}
